/**
 * Product Searchlight anchor helpers.
 *
 * Backend emits product cards keyed by anchor_id (e.g. pd_a1b2c3d4) via SSE
 * `product_display` events. Markdown may contain [PRODUCT_CARDS: pd_xxx] or legacy
 * [PRODUCTS: N] markers. The LLM sometimes writes [PRODUCTS: 30] using the
 * product *count* instead of a display index — these helpers recover gracefully.
 */

export type ProductDisplayMap = Record<string, unknown>;

const ANCHOR_MARKER_RE = /\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/g;

function displayKeys(productDisplays: ProductDisplayMap): string[] {
  return Object.keys(productDisplays || {});
}

function productsFromEntry(entry: unknown): unknown[] {
  if (!entry) return [];
  if (Array.isArray(entry)) return entry;
  if (typeof entry === 'object' && entry !== null && Array.isArray((entry as any).products)) {
    return (entry as any).products;
  }
  return [];
}

/** Resolve a markdown anchor token to a key in productDisplays. */
export function resolveProductDisplayKey(
  anchor: string,
  productDisplays: ProductDisplayMap,
  markerIndex = 0
): string | null {
  const keys = displayKeys(productDisplays);
  if (!keys.length) return null;

  const trimmed = (anchor || '').trim();
  if (productDisplays[trimmed] && productsFromEntry(productDisplays[trimmed]).length > 0) {
    return trimmed;
  }

  if (keys.length === 1) {
    const sole = keys[0];
    if (productsFromEntry(productDisplays[sole]).length > 0) {
      return sole;
    }
  }

  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed, 10);
    if (n >= 1 && n <= keys.length) {
      const byIndex = keys[n - 1];
      if (productsFromEntry(productDisplays[byIndex]).length > 0) {
        return byIndex;
      }
    }
  }

  if (trimmed.startsWith('pd_')) {
    const match = keys.find((k) => k === trimmed || k.toLowerCase() === trimmed.toLowerCase());
    if (match && productsFromEntry(productDisplays[match]).length > 0) {
      return match;
    }
  }

  if (markerIndex >= 0 && markerIndex < keys.length) {
    const byOrder = keys[markerIndex];
    if (productsFromEntry(productDisplays[byOrder]).length > 0) {
      return byOrder;
    }
  }

  const firstWithProducts = keys.find((k) => productsFromEntry(productDisplays[k]).length > 0);
  return firstWithProducts ?? null;
}

/** Rewrite bad [PRODUCTS: N] markers to [PRODUCT_CARDS: pd_xxx] when displays are known. */
export function normalizeProductAnchorsInContent(
  content: string,
  productDisplays: ProductDisplayMap
): string {
  const keys = displayKeys(productDisplays);
  if (!content || !keys.length) return content;

  let markerIndex = 0;
  return content.replace(ANCHOR_MARKER_RE, (match, raw: string) => {
    const resolved = resolveProductDisplayKey(raw, productDisplays, markerIndex);
    markerIndex += 1;
    if (!resolved) return match;
    return `[PRODUCT_CARDS: ${resolved}]`;
  });
}
